import { featureOn, timeAgo, type FeatureId, type Notification } from '@racksavant/core';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useMarkNotificationsRead, useNotifications } from '../lib/queries';
import { useAuthStore } from '../store/auth';
import { Avatar } from './Avatar';
import {
  IconBell,
  IconLooks,
  IconRack,
  IconRoom,
  IconRunway,
  IconSavant,
  IconStudio,
  IconUser,
} from './Icons';
import { IconButton } from './IconButton';
import { Sheet } from './Sheet';
import styles from './shell.module.css';

interface NavItem {
  to: string;
  label: string;
  short: string;
  icon: ReactNode;
  end?: boolean;
  /** Withheld until this flag comes back on. The route stays reachable either way. */
  feature?: FeatureId;
}

/**
 * Three sections and your own name on the door. The rack, the studio, your
 * looks — everything else is filed behind a flag until it is baked out. The
 * routes below still resolve; they simply have no entrance from here.
 */
const NAV: NavItem[] = [
  { to: '/app', label: 'The Rack', short: 'Rack', icon: <IconRack />, end: true },
  { to: '/app/studio', label: 'Studio', short: 'Studio', icon: <IconStudio /> },
  { to: '/app/looks', label: 'Your looks', short: 'Looks', icon: <IconLooks /> },
  { to: '/app/rooms', label: 'Fitting rooms', short: 'Rooms', icon: <IconRoom />, feature: 'rooms' },
  { to: '/app/runway', label: 'Runway', short: 'Runway', icon: <IconRunway />, feature: 'runway' },
  { to: '/app/savant', label: 'The Savant', short: 'Savant', icon: <IconSavant />, feature: 'savant' },
  { to: '/app/me', label: 'You', short: 'You', icon: <IconUser /> },
];

/**
 * Read at render, not at import: flipping a flag in `@racksavant/core` is the
 * whole change, and nothing here needs editing to go with it.
 */
export function visibleNav(): NavItem[] {
  return NAV.filter((item) => !item.feature || featureOn(item.feature));
}

export function AppShell({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const signOut = useAuthStore((state) => state.signOut);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const notifications = useNotifications(Boolean(user));
  const markRead = useMarkNotificationsRead();
  const unread = (notifications.data ?? []).filter((n) => !n.read).length;

  const nav = visibleNav();
  // The Savant has never had a phone tab, flag or no flag.
  const mobileNav = nav.filter((item) => item.to !== '/app/savant');

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const openNotifications = () => {
    setNotifOpen(true);
    if (unread > 0) markRead.mutate();
  };

  const credits = user?.credits ?? 0;

  return (
    <div className={styles.shell}>
      <a className="rs-skip-link" href="#main">
        Skip to content
      </a>

      <header className={styles.topbar}>
        {/* The masthead. A wordmark and one registration flash — no logo mark. */}
        <NavLink to="/app" className={styles.brand} aria-label="RackSavant home">
          RackSavant
          <span className={styles.brandMark} aria-hidden="true">
            *
          </span>
        </NavLink>

        <div className={styles.topbarSpacer} />

        <span
          className={[styles.credits, credits <= 2 ? styles.creditsLow : ''].filter(Boolean).join(' ')}
          title="Fittings left today"
        >
          <span className={styles.creditsDot} aria-hidden="true" />
          {credits} {credits === 1 ? 'fitting' : 'fittings'} left
        </span>

        <IconButton
          label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          icon={<IconBell />}
          badge={unread > 0}
          onClick={openNotifications}
        />

        <div ref={menuRef} style={{ position: 'relative', display: 'flex' }}>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Your account"
            style={{ display: 'flex' }}
          >
            <Avatar
              name={user?.displayName ?? 'You'}
              src={user?.avatarUrl}
              size={34}
              mood={user?.signatureMood ?? null}
            />
          </button>

          {menuOpen ? (
            <div className={styles.menu} role="menu">
              <div className={styles.menuHeader}>
                <div className={styles.menuName}>{user?.displayName}</div>
                <div className={styles.menuHandle}>@{user?.username}</div>
              </div>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/app/me');
                }}
              >
                Profile & photos
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => {
                  setMenuOpen(false);
                  if (user) navigate(`/u/${user.username}`);
                }}
              >
                View public profile
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/pricing');
                }}
              >
                Plans
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => {
                  setMenuOpen(false);
                  void signOut().then(() => navigate('/'));
                }}
              >
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </header>

      <div className={styles.body}>
        <nav className={styles.rail} aria-label="Studio">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                [styles.railLink, isActive ? styles.railLinkActive : ''].filter(Boolean).join(' ')
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
          <div className={styles.railFooter}>
            Your photos stay private until you say otherwise.
          </div>
        </nav>

        <main id="main" className={styles.main} tabIndex={-1}>
          {children}
        </main>
      </div>

      <nav className={styles.tabbar} aria-label="Studio">
        {mobileNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              [styles.tabLink, isActive ? styles.tabLinkActive : ''].filter(Boolean).join(' ')
            }
          >
            {item.icon}
            {item.short}
            <span className={styles.tabDot} aria-hidden="true" />
          </NavLink>
        ))}
      </nav>

      <Sheet open={notifOpen} onClose={() => setNotifOpen(false)} title="What happened">
        <NotificationList
          notifications={notifications.data ?? []}
          onOpen={(notification) => {
            setNotifOpen(false);
            if (notification.lookId) navigate(`/app/looks/${notification.lookId}`);
            // Not a CTA — this is a deep link somebody sent, and it keeps
            // working while rooms are withheld from the navigation.
            else if (notification.roomCode) navigate(`/app/rooms/${notification.roomCode}`);
          }}
        />
      </Sheet>
    </div>
  );
}

function NotificationList({
  notifications,
  onOpen,
}: {
  notifications: Notification[];
  onOpen(notification: Notification): void;
}) {
  if (notifications.length === 0) {
    return <p className="rs-u-muted">All caught up. Hype, invites and finished fittings show up here.</p>;
  }

  return (
    <div className={styles.notifList}>
      {notifications.map((notification) => (
        <button
          key={notification.id}
          type="button"
          className={[styles.notifItem, notification.read ? '' : styles.notifUnread].filter(Boolean).join(' ')}
          onClick={() => onOpen(notification)}
        >
          <Avatar name={notification.actor?.displayName ?? 'RackSavant'} src={notification.actor?.avatarUrl} size={32} />
          <span className={styles.notifText}>
            {notification.text}
            <br />
            <span className={styles.notifTime}>{timeAgo(notification.createdAt)}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
